import React, { useEffect, useState } from 'react';
import { Grid, Stack, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { GlassButton } from 'dashboard/components/common';

/**
 * The signed-in person's own details. Nothing here belongs to a brand.
 */
export default function AccountProfileTab({ profile, onSaved }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', phone_number: '', city: '', country: '' });
  const [busy, setBusy] = useState(false);
  /**
   * The username is kept apart from the rest of the form.
   *
   * It lives on the user record rather than the profile, it is unique across
   * the platform, and it can fail for a reason none of the other fields can
   * — someone else has it. Saving it with the profile would mean one button
   * that half-succeeds, so it has its own field and its own save.
   */
  const [username, setUsername] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    setForm({
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      phone_number: profile?.phone_number || '',
      city: profile?.city || '',
      country: profile?.country || '',
    });
    setUsername(profile?.username || '');
  }, [profile]);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const save = () => {
    setBusy(true);
    // The endpoint takes multipart because it also accepts an avatar upload.
    const body = new FormData();
    Object.entries(form).forEach(([k, v]) => body.append(k, v ?? ''));
    API.updateUserProfile(body)
      .then(() => { toast.success('Profile saved'); onSaved?.(); })
      .catch((err) => toast.error(err?.message || 'Could not save your profile'))
      .finally(() => setBusy(false));
  };

  const saveUsername = () => {
    setSavingName(true);
    API.changeUsername({ username })
      .then((res) => {
        toast.success(res?.username ? 'Username updated' : 'Username removed');
        onSaved?.();
      })
      .catch((err) => toast.error(err?.message || 'Could not change your username'))
      .finally(() => setSavingName(false));
  };

  const usernameChanged = (username || '') !== (profile?.username || '');

  return (
    <Stack spacing='18px'>
      <Typography variant='body2' color='text.secondary'>
        Your sign-in email is <strong>{profile?.email || '—'}</strong>. This is your personal
        profile — it is not shown on any brand's storefront.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing='10px' alignItems={{ sm: 'flex-start' }}>
        <TextField
          fullWidth
          size='small'
          label='Username'
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          helperText='Letters, numbers, dots, dashes and underscores. Leave blank to have none.'
        />
        <GlassButton
          onClick={saveUsername}
          disabled={savingName || !usernameChanged}
          disabledReason={!usernameChanged ? 'Nothing to change yet.' : ''}
          sx={{ flexShrink: 0, mt: { sm: '2px' } }}
        >
          {savingName ? 'Saving…' : 'Change'}
        </GlassButton>
      </Stack>

      <Grid container spacing='16px'>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size='small' label='First name' value={form.first_name} onChange={set('first_name')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size='small' label='Last name' value={form.last_name} onChange={set('last_name')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size='small' label='Phone' value={form.phone_number} onChange={set('phone_number')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size='small' label='City' value={form.city} onChange={set('city')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size='small' label='Country' value={form.country} onChange={set('country')} />
        </Grid>
      </Grid>

      <Stack direction='row' justifyContent='flex-end'>
        <GlassButton variant='contained' onClick={save} disabled={busy} sx={{ borderRadius: '14px', px: '26px', height: 44 }}>
          {busy ? 'Saving…' : 'Save changes'}
        </GlassButton>
      </Stack>
    </Stack>
  );
}
